import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, PlayCircle, ChevronRight, Clock, ThumbsUp, Share2, CheckCircle2, Sparkles } from "lucide-react";

export default function OdontogramaTutorialPage() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img src="/marketing/logo_official.png" className="h-28 object-contain" alt="OdontoAssist AI Logo" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/ajuda/tutoriais" className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block">
              Central de Guias
            </Link>
            <Link 
              to="/" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Clínico</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Como preencher o Odontograma Interativo
        </h1>
        
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} /> <span>3 min de leitura / vídeo</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão Clínica
          </div>
        </div>

        {/* Video Player */}
        <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl mb-12 border-4 border-white bg-black">
          <iframe 
            width="100%" 
            height="100%" 
            src="https://www.youtube.com/embed/azVqeaKyJyU" 
            title="Como preencher o Odontograma Interativo" 
            frameBorder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            referrerPolicy="strict-origin-when-cross-origin" 
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          ></iframe>
        </div>

        <div className="flex items-center justify-between border-b border-gray-200 pb-8 mb-10">
          <div className="flex items-center gap-4">
            <img src="https://i.pravatar.cc/150?img=32" alt="Instrutora" className="w-12 h-12 rounded-full border-2 border-white shadow-sm" />
            <div>
              <p className="font-bold text-gray-900">Dra. Camila Marques</p>
              <p className="text-sm text-gray-500">Especialista em Sucesso do Cliente</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <ThumbsUp size={18} /> Útil
            </button>
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <Share2 size={18} /> Compartilhar
            </button>
          </div>
        </div>

        {/* Written Tutorial */}
        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            O odontograma interativo é o coração do prontuário do OdontoAssist. Ele permite registrar a situação inicial do paciente de forma gráfica, planejar tratamentos e marcar os procedimentos realizados.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Passo a Passo em Texto</h2>
          
          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                 Iniciando um Atendimento
               </h3>
               <p>Acesse o prontuário de um paciente e clique na aba <strong>"Odontograma"</strong>. A visão padrão exibe as arcadas dentárias superior e inferior. Cada dente possui faces clicáveis representadas nas imagens.</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                 Selecionando o Dente e a Face
               </h3>
               <p>Passe o mouse por cima do dente desejado. Você verá que ele brilha indicando interatividade. Clique diretamente na face específica (Vestibular, Palatina/Lingual, Mesial, Distal ou Oclusal/Incisal) onde o procedimento será realizado.</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                 Adicionando o Procedimento
               </h3>
               <p>Após clicar na face, um menu lateral ou balão irá se abrir. Você poderá escolher o Status (Planejado, Realizado ou Condição Inicial). Selecione o procedimento a partir da busca — por exemplo: <em>Restauração em Resina Composta</em>.</p>
               <div className="mt-4 bg-amber-50 p-4 rounded-xl border border-amber-200">
                 <p className="text-amber-800 text-sm mb-0"><strong>Importante:</strong> Se você selecionar a opção "Condição Inicial", a cor ficará gravada para demonstrar que essa era a situação que o paciente chegou na sua clínica.</p>
               </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">4</span>
                 Acompanhando a Evolução
               </h3>
               <p>Ao finalizar e salvar o procedimento, o odontograma será colorido simbolizando aquele tratamento. Tudo o que for gravado no odontograma irá automaticamente para a aba <strong>Evolução Clínica</strong> em modo texto, poupando você de ter que digitar as anotações principais repetidamente.</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-purple-200 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-purple-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-black">5</span>
                 Revisando as Marcações com IA
               </h3>
               <p>Após registrar condições, faces, procedimentos ou observações, use <strong>Revisar marcações</strong>. A IA organiza somente os dados estruturados e aponta possíveis incoerências de preenchimento para sua conferência.</p>
               <div className="mt-4 bg-purple-50 p-4 rounded-xl border border-purple-200 flex items-start gap-3">
                 <Sparkles className="text-purple-600 shrink-0 mt-1" size={20} />
                 <p className="text-purple-900 text-sm mb-0"><strong>Não há análise de radiografias ou fotos.</strong> A revisão não diagnostica, não prioriza tratamentos e não modifica o odontograma.</p>
               </div>
            </div>
          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
             <h3 className="text-2xl font-bold mb-4">Uso Seguro da Inteligência Artificial</h3>
             <ul className="space-y-3">
               <li className="flex items-start gap-3">
                 <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                 <span>A IA utiliza exclusivamente as marcações estruturadas do odontograma para organizar o registro. Mantenha condições, faces, procedimentos e observações atualizados para obter uma revisão útil.</span>
               </li>
             </ul>
          </div>
        </article>
      </main>
    </div>
  );
}
